import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { ArrowLeft, Calendar, Loader2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useAuditLog } from "~/hooks/contabilidad/useAuditLog";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";

// Generate month options for the last 24 months
function generateMonthOptions() {
  const options = [];
  const now = dayjs();
  for (let i = 0; i < 24; i++) {
    const date = now.subtract(i, "month");
    options.push({
      value: date.format("YYYY-MM"),
      label: date.format("MMMM YYYY"),
      year: date.year(),
      month: date.month() + 1,
    });
  }
  return options;
}

export default function AuditLogPage() {
  const navigate = useNavigate();
  const monthOptions = useMemo(() => generateMonthOptions(), []);

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");

  const { data: companies = [] } = useCompanies();

  // Get year and month from selected month
  const selectedOption = monthOptions.find(
    (opt) => opt.value === selectedMonth
  );

  const { data: auditLogs = [], isLoading } = useAuditLog({
    year: selectedOption?.year,
    month: selectedOption?.month,
    companyId: companyFilter === "all" ? undefined : companyFilter,
    action:
      actionFilter === "all"
        ? undefined
        : (actionFilter as "publish" | "unpublish"),
  });

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/contabilidad/reportes")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Auditoría de Publicaciones</h1>
          <p className="text-sm text-muted-foreground">
            Historial de acciones de publicar/despublicar comprobantes
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Período</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[200px] rounded-xl">
                  <Calendar className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Seleccionar período" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Empresa</Label>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className="w-[200px] rounded-xl">
                  <SelectValue placeholder="Filtrar por empresa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Acción</Label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[200px] rounded-xl">
                  <SelectValue placeholder="Filtrar por acción" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las acciones</SelectItem>
                  <SelectItem value="publish">Publicar</SelectItem>
                  <SelectItem value="unpublish">Despublicar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Historial de Acciones</CardTitle>
          <p className="text-sm text-muted-foreground">
            {auditLogs.length} registros encontrados
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No hay registros de auditoría para el período seleccionado
            </div>
          ) : (
            <div className="rounded-xl border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº Comprobante</TableHead>
                    <TableHead>Fecha del Comprobante</TableHead>
                    <TableHead>Fecha de Acción</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log, index) => (
                    <TableRow
                      key={log.id}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                    >
                      <TableCell className="font-medium">
                        {log.move_number}
                      </TableCell>
                      <TableCell>
                        {dayjs(log.move_date).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell>
                        {dayjs(log.performed_at).format("DD/MM/YYYY HH:mm")}
                      </TableCell>
                      <TableCell>
                        {log.performer
                          ? `${log.performer.name} ${log.performer.lastname}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            log.action === "publish" ? "default" : "secondary"
                          }
                        >
                          {log.action === "publish"
                            ? "Publicar"
                            : "Despublicar"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
